import { useCallback, useRef } from 'react';
import { appMessage as message } from '../utils/appMessage';
import { blockedText, conflictReason } from '../http';

/**
 * "잡 시작 API 호출 → activeJobId 세팅 → 실패 시 토스트" 공통 뼈대(2026-08-11).
 * `useCardJobs`(UseCardData.ts)와 `StockPageContext`가 각자 손으로 이 try/catch를
 * 반복 구현하고 있어 여기로 모은다.
 *
 * @param setActiveJobId 잡이 시작되면 진행바(JobProgressBar)가 폴링을 시작할 수 있게
 *                        jobId를 세팅하는 함수.
 */
export function useJobRunner(setActiveJobId: (jobId: string) => void) {
  // 잡 시작 요청이 응답을 기다리는 동안엔 두 번째 시작을 막는다(2026-09-21).
  // 버튼을 잠그는 상태(naverSyncing 등)는 onStarted?.() — 즉 POST가 "돌아온 뒤"에만 켜지기
  // 때문에, 그 사이 연타하면 클릭 수만큼 잡이 만들어진다. 실제로 "③ 스토어 반영"을 10번
  // 연타해 SYNC_NAVER 잡 10개가 생성되고 그중 4개(jobExecutor max pool)가 동시에 돌아,
  // 같은 카드에 같은 가격 PUT이 10번씩 나가고 반영 로그도 10배로 쌓인 적이 있다. 게다가
  // activeJobId는 마지막 응답 것으로 덮이므로, 진행바가 그 하나만 보고 "반영 완료"를 띄운
  // 뒤에도 나머지 잡이 백그라운드에서 계속 네이버에 쓰고 있었다.
  const inFlightRef = useRef(false);

  const runJob = useCallback(async (
    startFn: () => Promise<{ jobId: string }>,
    errorMessage: string,
    onStarted?: () => void,
    /**
     * 409(반영 막힘 · 잡 락)를 "<conflictLabel> — <사유>" 경고로 띄운다(감사 card-controltower#700, 백엔드 PR #82 — 확정 대기 1,000건
     * 잘림이면 ③ · 롤백이 409). 없으면 예전처럼 서버 문구를 오류 토스트로. 403(권한)은 http 인터셉터가 따로 띄운다.
     */
    opts?: { conflictLabel?: string },
  ): Promise<boolean> => {
    if (inFlightRef.current) return false;
    inFlightRef.current = true;
    try {
      const { jobId } = await startFn();
      setActiveJobId(jobId);
      onStarted?.();
      return true;
    } catch (e) {
      const blocked = opts?.conflictLabel ? conflictReason(e) : null;
      if (blocked && opts?.conflictLabel) {
        message.warning({ content: blockedText(blocked, opts.conflictLabel), duration: 8 });
        return false;
      }
      // 백엔드가 이유를 담아 거절하면(예: "이미 이 매장의 상품 등록 작업이 진행 중입니다" 409)
      // 그 메시지를 그대로 보여준다 — 일반 문구보다 사용자가 다음 행동을 판단하기 쉽다.
      const serverMessage = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(serverMessage || errorMessage);
      return false;
    } finally {
      inFlightRef.current = false;
    }
  }, [setActiveJobId]);

  return runJob;
}
